import { z } from "zod";
import { useContext } from "react";
import { ModelDefinition, ValidationMessage, validationMessage } from "../base-components/hooks/useModel";
import { Context } from "../App";
import { RoutePath } from "@/generic/config";
import { ProductSchema } from "./product";
import { createOrder, getOrder, saveOrder } from "@/api/order-controller";
import { emptyWorksiteObject, WorksiteSchema } from "./worksite";
import { Translations } from "@/generic/Translations";
import { AttachedFileSchema } from "./attached-file.schema";
import { isEmptyDate, isSameDay } from "@/generic/date-utils";
import { PropertyValueSchema } from "./property";

export const emptyDate = new Date("1900-01-01");
export const EXPRESS_PRIORITY = 9223372036854775807; // Max safe signed 64bit integer which means "express priority"

export enum OrderStatus {
    CREATED = "CREATED",
    CANCELLED = "CANCELLED",
    COMPLETED = "COMPLETED",
    INVOICED = "INVOICED",
    OFFERED = "OFFERED",
}

export const ALL_ORDER_STATUSES = Object.values(OrderStatus);
export const ORDER_STATUS_OPTIONS = (translations: Translations) => ALL_ORDER_STATUSES.map(status => ({ id: status, name: translations.model.order.statusOptions[status] }));

export type SortOrder = "SCHEDULED_DESC" | "SCHEDULED_ASC" | "COMPLETION_DESC" | "COMPLETION_ASC" | "CREATED_DESC";
export const SORT_ORDER_OPTIONS = (translations: Translations) => ([
    { id: "CREATED_DESC" as SortOrder, name: translations.model.order.sortOrderOptions.CREATED_DESC },
    { id: "SCHEDULED_DESC" as SortOrder, name: translations.model.order.sortOrderOptions.SCHEDULED_DESC },
    { id: "SCHEDULED_ASC" as SortOrder, name: translations.model.order.sortOrderOptions.SCHEDULED_ASC },
    { id: "COMPLETION_DESC" as SortOrder, name: translations.model.order.sortOrderOptions.COMPLETION_DESC },
    { id: "COMPLETION_ASC" as SortOrder, name: translations.model.order.sortOrderOptions.COMPLETION_ASC },
]);

export const OrderRowSchema = z.object({
    id: z.number(),
    rowId: z.number(),
    product: ProductSchema,
    orderedQuantity: z.number(),
    actualQuantity: z.number(),
});

export type OrderRow = z.infer<typeof OrderRowSchema>;


export const OrderSchema = z.object({
    id: z.number(),
    externalId: z.string(),
    worksite: WorksiteSchema,
    orderRows: z.array(OrderRowSchema),
    scheduledDate: z.date(),
    completionDate: z.date(),
    express: z.boolean(),
    priority: z.number(),
    invoiced: z.boolean(),
    recurringInterval: z.number(),
    contractEndDate: z.date(),
    blockedReason: z.string(),
    blocked: z.boolean(),
    dispatcherNotes: z.string(),
    employeeNotes: z.string(),
    resourceId: z.number().optional(),
    status: z.nativeEnum(OrderStatus),
    attachedFiles: z.array(AttachedFileSchema),
    propertyValues: z.array(PropertyValueSchema),
    extraData: z.object({
        scheduledDateOverrides: z.record(z.string(), z.string()).optional(),
    }).catchall(z.unknown()).optional(),
});

type Order = z.infer<typeof OrderSchema>;

export default Order;

const ValidationMessages = (model: Order, isFromTasks: boolean) => {
    const { translations } = useContext(Context);
    const rules = translations.model.order.validationRules;
    const messages: ValidationMessage<Order>[] = [];

    // if (!model.externalId || model.externalId.trim() === "") {
    //     messages.push(validationMessage("externalId", rules.shouldHaveExternalId, "warning"));
    // }

    if (!model.worksite || !model.worksite.id) {
        messages.push(validationMessage("worksite", rules.worksiteIsRequired, "error"));
    }

    if (isEmptyDate(model.scheduledDate)) {
        messages.push(validationMessage("scheduledDate", rules.scheduledDateIsRequired, "error"));
    }

    if (isFromTasks) {
        if (model.status !== OrderStatus.COMPLETED) {
            messages.push(validationMessage("status", rules.fromTasks.reminderComplete, "warning"));
        }

        if (model.status === OrderStatus.COMPLETED) {
            const incompleteRows = model.orderRows.filter(row => row.orderedQuantity !== row.actualQuantity);
            if (incompleteRows.length > 0) {
                messages.push(validationMessage("orderRows", rules.fromTasks.rowsShouldMatch, "warning"));
            }

            if (!isSameDay(model.completionDate, new Date())) {
                messages.push(validationMessage("completionDate", rules.fromTasks.reminderCompletionDateShouldBeToday, "warning"));
            }
        }
    }

    if (model.status === OrderStatus.COMPLETED && (isEmptyDate(model.completionDate) || !model.completionDate)) {
        messages.push(validationMessage("completionDate", rules.completionDateMustBeSetWhenCompleted, "error"));
    }

    if (![OrderStatus.COMPLETED, OrderStatus.INVOICED, OrderStatus.CANCELLED].includes(model.status) && !isEmptyDate(model.completionDate)) {
        messages.push(validationMessage("completionDate", rules.completionDateShouldOnlyBeSetWhenCompleted, "error"));
    }

    if (model.id <= 0 && model.worksite.active === false) {
        messages.push(validationMessage("worksite", rules.selectedWorksiteIsInactive, "error"));
    }

    return messages;
};

const emptyOrderObject: Order = {
    id: 0,
    externalId: "",
    worksite: WorksiteSchema.parse(emptyWorksiteObject),
    orderRows: [],
    scheduledDate: emptyDate,
    completionDate: emptyDate,
    express: false,
    priority: 0,
    invoiced: false,
    recurringInterval: 0,
    contractEndDate: emptyDate,
    blockedReason: "",
    blocked: false,
    dispatcherNotes: "",
    employeeNotes: "",
    resourceId: 0,
    status: OrderStatus.CREATED,
    attachedFiles: [],
    propertyValues: [],
    extraData: undefined,
};

export const OrderModel = (isFromTasks: boolean): ModelDefinition<Order> => ({
    getById: getOrder,
    saveFromModel: saveOrder,
    createFromModel: createOrder,
    validationMessages: (model) => ValidationMessages(model, isFromTasks),
    routePath: RoutePath.Orders,
    typeProviderObject: OrderSchema.parse(emptyOrderObject),
});

export const getOrderSummary = (order: Order) => {
    return `Order ${order.externalId} at ${order.worksite.name} scheduled for ${order.scheduledDate.toDateString()}`;
};